/**
 * FtoClaimsAnalysisPane — "Claims Analysis" tab in the FTO workspace.
 *
 * Shows the full per-feature analysis for a selected patent claim, including:
 * - Two selectors: patent + independent claim
 * - Claim-level verdict (verdict, confidence, rationale)
 * - Per-feature blocks: classification, relation, score, is_present, confidence,
 *   FCM, evidence, LLM rationale
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ftoAnalysisService,
  type ClaimAnalysisArtifact,
  type FtoReport,
  type FtoClaimReport,
  type FtoCostEstimate,
  type RefinedFeature,
  type FeatureMapping,
  type FeatureVerdict,
} from '../services/ftoAnalysisService';

function apiErrorDetail(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

interface FtoClaimsAnalysisPaneProps {
  subProjectId: string;
  solutionId: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const CLASS_LABEL: Record<string, string> = {
  clear_structural: 'Structural',
  clear_functional: 'Functional',
  vague_or_relative: 'Vague/Relative',
  means_plus_function: 'Means+Function',
  markush_or_alternative: 'Markush/Alt.',
};

const CLASS_COLOR: Record<string, string> = {
  clear_structural:      'bg-blue-100 text-blue-700',
  clear_functional:      'bg-purple-100 text-purple-700',
  vague_or_relative:     'bg-yellow-100 text-yellow-700',
  means_plus_function:   'bg-orange-100 text-orange-700',
  markush_or_alternative:'bg-teal-100 text-teal-700',
};

const RELATION_LABEL: Record<string, string> = {
  conjunctive: 'Conj.',
  alternative: 'Alt.',
  optional:    'Opt.',
};

const RELATION_COLOR: Record<string, string> = {
  conjunctive: 'text-slate-500',
  alternative: 'text-teal-600',
  optional:    'text-slate-400 italic',
};

function markColor(mark: number): string {
  if (mark <= 1) return 'bg-red-100 text-red-700';
  if (mark <= 2) return 'bg-orange-100 text-orange-700';
  if (mark <= 3) return 'bg-yellow-100 text-yellow-700';
  return 'bg-green-100 text-green-700';
}

// Normalize verdicts to a tier key; legacy reports used "infringed" / "not infringed"
function verdictTier(verdict: string): 'highRisk' | 'reviewRequired' | 'lowRisk' | 'unknown' {
  const v = verdict?.toLowerCase();
  if (v === 'high_risk' || v === 'infringed') return 'highRisk';
  if (v === 'review_required') return 'reviewRequired';
  if (v === 'low_risk' || v === 'not infringed') return 'lowRisk';
  return 'unknown';
}

const TIER_COLOR: Record<string, string> = {
  highRisk: 'bg-red-100 text-red-700 border-red-200',
  reviewRequired: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  lowRisk: 'bg-green-100 text-green-700 border-green-200',
  unknown: 'bg-slate-100 text-slate-600 border-slate-200',
};

// ── Launch with cost estimate ─────────────────────────────────────────────────

const LaunchWithEstimate: React.FC<{
  subProjectId: string;
  solutionId: string;
  onLaunched: () => void;
}> = ({ subProjectId, solutionId, onLaunched }) => {
  const { t } = useTranslation();
  const [estimate, setEstimate] = useState<FtoCostEstimate | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestEstimate = async () => {
    setBusy(true);
    setError(null);
    try {
      setEstimate(await ftoAnalysisService.getEstimate(subProjectId, solutionId));
    } catch (e) {
      setError(apiErrorDetail(e, t('ftoClaimsAnalysis.estimateError', 'Failed to compute the cost estimate.')));
    } finally {
      setBusy(false);
    }
  };

  const confirmLaunch = async () => {
    setBusy(true);
    setError(null);
    try {
      await ftoAnalysisService.launch(subProjectId, solutionId);
      setEstimate(null);
      onLaunched();
    } catch (e) {
      setError(apiErrorDetail(e, t('ftoClaimsAnalysis.launchError', 'Failed to launch FTO analysis.')));
    } finally {
      setBusy(false);
    }
  };

  const skipped = estimate ? estimate.patent_count - estimate.patents_with_claims : 0;

  return (
    <div className="flex flex-col items-center gap-2">
      {!estimate ? (
        <button
          onClick={requestEstimate}
          disabled={busy}
          className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? t('common.loading', 'Loading...') : t('ftoClaimsAnalysis.launch', 'Launch FTO Analysis')}
        </button>
      ) : (
        <div className="border border-slate-200 rounded p-3 flex flex-col gap-2 bg-slate-50 max-w-sm">
          <p className="text-xs text-slate-700">
            {t(
              'ftoClaimsAnalysis.estimateSummary',
              '{{patents}} patent(s) will be analyzed (~{{calls}} LLM calls, estimated cost {{cost}} {{currency}}, model {{model}}).',
              {
                patents: estimate.patents_with_claims,
                calls: estimate.estimated_llm_calls,
                cost: (estimate.estimated_cost_cents / 100).toFixed(2),
                currency: estimate.currency,
                model: estimate.model_name || estimate.model_id,
              },
            )}
          </p>
          {skipped > 0 && (
            <p className="text-xs text-orange-600">
              {t('ftoClaimsAnalysis.estimateMissingClaims', '{{n}} patent(s) have no claims text and will be skipped.', { n: skipped })}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={confirmLaunch}
              disabled={busy}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? t('ftoClaimsAnalysis.launching', 'Launching...') : t('ftoClaimsAnalysis.confirmLaunch', 'Confirm and launch')}
            </button>
            <button
              onClick={() => { setEstimate(null); setError(null); }}
              disabled={busy}
              className="px-3 py-1.5 text-xs border border-slate-300 text-slate-500 rounded hover:bg-slate-100"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-500 max-w-sm text-center">{error}</p>}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const FtoClaimsAnalysisPane: React.FC<FtoClaimsAnalysisPaneProps> = ({
  subProjectId,
  solutionId,
}) => {
  const { t } = useTranslation();

  const [report, setReport] = useState<FtoReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [reportError, setReportError] = useState<string | null>(null);

  const [selectedPatentId, setSelectedPatentId] = useState<string>('');
  const [selectedClaimIndex, setSelectedClaimIndex] = useState<number | null>(null);

  const [showRelaunch, setShowRelaunch] = useState(false);
  const [artifact, setArtifact] = useState<ClaimAnalysisArtifact | null>(null);
  const [loadingArtifact, setLoadingArtifact] = useState(false);
  const [artifactError, setArtifactError] = useState<string | null>(null);

  // Cache artifacts to avoid re-fetching
  const artifactCache = useRef<Record<string, ClaimAnalysisArtifact>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load report (+ polling while running) ───────────────────────────────────
  const fetchReport = useCallback(async () => {
    try {
      const r = await ftoAnalysisService.getReport(subProjectId, solutionId);
      setReport(r);
      setReportError(null);
      return r;
    } catch {
      setReportError(t('ftoClaimsAnalysis.noReport', 'No FTO report found. Launch the FTO analysis first.'));
      return null;
    }
  }, [subProjectId, solutionId, t]);

  useEffect(() => {
    setLoadingReport(true);
    fetchReport().finally(() => setLoadingReport(false));
  }, [fetchReport]);

  // Poll every 5s while status === "running"
  useEffect(() => {
    if (report?.status === 'running') {
      pollRef.current = setInterval(async () => {
        const r = await fetchReport();
        if (r && r.status !== 'running') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 5000);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [report?.status, fetchReport]);

  // ── Derive data from selections ─────────────────────────────────────────────
  const patentReport = report?.patents.find(p => p.patent_id === selectedPatentId) ?? null;

  const independentClaims: FtoClaimReport[] = patentReport?.claims_analysed ?? [];

  const selectedClaimReport: FtoClaimReport | null =
    selectedClaimIndex !== null
      ? (independentClaims.find(c => c.claim_index === selectedClaimIndex) ?? null)
      : null;

  // ── Load artifact when claim selected ───────────────────────────────────────
  const loadArtifact = useCallback(async (artifactId: string) => {
    if (artifactCache.current[artifactId]) {
      setArtifact(artifactCache.current[artifactId]);
      return;
    }
    setLoadingArtifact(true);
    setArtifactError(null);
    try {
      const a = await ftoAnalysisService.getClaimArtifact(subProjectId, artifactId);
      artifactCache.current[artifactId] = a;
      setArtifact(a);
    } catch {
      setArtifactError(t('ftoClaimsAnalysis.artifactError', 'Failed to load claim features.'));
    } finally {
      setLoadingArtifact(false);
    }
  }, [subProjectId, t]);

  useEffect(() => {
    if (selectedClaimReport?.artifact_id) {
      loadArtifact(selectedClaimReport.artifact_id);
    } else {
      setArtifact(null);
    }
  }, [selectedClaimReport, loadArtifact]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleLaunched = () => {
    setReportError(null);
    // Set a synthetic running state — the polling useEffect takes over from here.
    setReport({ id: '', solution_id: solutionId, status: 'running', patents: [], solution_html_hash: '', countries: [], country_matrix: {}, created_at: new Date().toISOString(), model_ids_used: [] });
  };

  const handlePatentChange = (patentId: string) => {
    setSelectedPatentId(patentId);
    setSelectedClaimIndex(null);
    setArtifact(null);
  };

  const handleClaimChange = (claimIndex: string) => {
    setSelectedClaimIndex(claimIndex === '' ? null : parseInt(claimIndex, 10));
    setArtifact(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loadingReport) {
    return (
      <div className="flex items-center gap-2 p-4 text-xs text-slate-400">
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  if (reportError && !report) {
    return (
      <div className="flex flex-col items-center gap-3 p-6">
        <p className="text-xs text-slate-500 italic">{reportError}</p>
        <LaunchWithEstimate subProjectId={subProjectId} solutionId={solutionId} onLaunched={handleLaunched} />
      </div>
    );
  }

  if (report?.status === 'failed') {
    return (
      <div className="flex flex-col items-center gap-3 p-6">
        <p className="text-xs text-red-500 italic">
          {report.error || t('ftoClaimsAnalysis.failed', 'Analysis failed. Check LLM configuration in Settings, then re-launch.')}
        </p>
        <LaunchWithEstimate subProjectId={subProjectId} solutionId={solutionId} onLaunched={handleLaunched} />
      </div>
    );
  }

  const patents = report?.patents ?? [];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Selectors strip */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50 flex-shrink-0 flex-wrap">
        <div className="flex items-center gap-1.5">
          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
            {t('ftoClaimsAnalysis.patentLabel', 'Patent')}
          </label>
          <select
            className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700 outline-none focus:border-blue-400 min-w-[160px]"
            value={selectedPatentId}
            onChange={e => handlePatentChange(e.target.value)}
          >
            <option value="">{t('ftoClaimsAnalysis.selectPatent', '— select —')}</option>
            {patents.map(p => (
              <option key={p.patent_id} value={p.patent_id}>
                {p.patent_id} — {t(`ftoClaimsAnalysis.verdict.${verdictTier(p.worst_verdict)}`, p.worst_verdict)}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
            {t('ftoClaimsAnalysis.claimLabel', 'Claim')}
          </label>
          <select
            className="text-xs border border-slate-300 rounded px-2 py-1 bg-white text-slate-700 outline-none focus:border-blue-400 min-w-[120px] disabled:opacity-50"
            value={selectedClaimIndex ?? ''}
            onChange={e => handleClaimChange(e.target.value)}
            disabled={!selectedPatentId}
          >
            <option value="">{t('ftoClaimsAnalysis.selectClaim', '— select —')}</option>
            {independentClaims.map(c => (
              <option key={c.claim_index} value={c.claim_index}>
                {t('ftoClaimsAnalysis.claimN', 'Claim {{n}}', { n: c.claim_index })}
              </option>
            ))}
          </select>
        </div>

        {/* Running indicator */}
        {report?.status === 'running' && (
          <div className="flex items-center gap-1.5 ml-auto">
            <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-[10px] text-blue-600 font-medium">
              {t('ftoClaimsAnalysis.running', 'Analysis in progress...')}
              {patents.length > 0 && ` (${patents.length} patent${patents.length > 1 ? 's' : ''})`}
            </span>
          </div>
        )}

        {/* Re-launch (completed reports only) */}
        {report?.status === 'completed' && (
          <button
            onClick={() => setShowRelaunch(v => !v)}
            className="ml-auto text-[10px] px-2 py-0.5 rounded border border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600"
          >
            {t('ftoClaimsAnalysis.relaunch', 'Re-launch analysis')}
          </button>
        )}
      </div>

      {/* Re-launch panel with cost estimate */}
      {showRelaunch && report?.status === 'completed' && (
        <div className="px-3 py-3 border-b border-slate-200 bg-slate-50/50 flex-shrink-0">
          <LaunchWithEstimate
            subProjectId={subProjectId}
            solutionId={solutionId}
            onLaunched={() => { setShowRelaunch(false); handleLaunched(); }}
          />
        </div>
      )}

      {/* Main content area */}
      <div className="flex-1 overflow-auto px-3 py-3">
        {!selectedPatentId || selectedClaimIndex === null ? (
          <p className="text-xs text-slate-400 italic mt-2">
            {t('ftoClaimsAnalysis.selectPrompt', 'Select a patent and a claim to view the analysis.')}
          </p>
        ) : loadingArtifact ? (
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            {t('common.loading', 'Loading...')}
          </div>
        ) : artifactError ? (
          <p className="text-xs text-red-500 mt-2">{artifactError}</p>
        ) : selectedClaimReport ? (
          <ClaimAnalysisView
            claimReport={selectedClaimReport}
            artifact={artifact}
            t={t}
          />
        ) : null}
      </div>
    </div>
  );
};

// ── ClaimAnalysisView ─────────────────────────────────────────────────────────

interface ClaimAnalysisViewProps {
  claimReport: FtoClaimReport;
  artifact: ClaimAnalysisArtifact | null;
  t: (key: string, defaultValue: string, opts?: Record<string, unknown>) => string;
}

const ClaimAnalysisView: React.FC<ClaimAnalysisViewProps> = ({ claimReport, artifact, t }) => {
  const verdict = claimReport.verdict;

  // Build lookup maps for quick join
  const mappingById: Record<string, FeatureMapping> = {};
  (claimReport.mapping?.mappings ?? []).forEach(m => { mappingById[m.feature_id] = m; });

  const verdictById: Record<string, FeatureVerdict> = {};
  (verdict?.feature_verdicts ?? []).forEach(v => { verdictById[v.feature_id] = v; });

  const features: RefinedFeature[] = artifact?.features ?? [];

  return (
    <div className="flex flex-col gap-4">

      {/* ── Claim-level verdict ── */}
      {verdict ? (
        <div className={`rounded border px-3 py-2 ${TIER_COLOR[verdictTier(verdict.verdict)]}`}>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-bold uppercase tracking-wide">
              {t(`ftoClaimsAnalysis.verdict.${verdictTier(verdict.verdict)}`, verdict.verdict)}
            </span>
            <span className="text-xs font-medium opacity-80">
              {t('ftoClaimsAnalysis.confidence', 'Confidence')}: {verdict.confidence}%
            </span>
          </div>
          {verdictTier(verdict.verdict) === 'reviewRequired' && (
            <p className="text-xs font-semibold mb-1">
              {t('ftoClaimsAnalysis.reviewNotice', 'This claim must be assessed by a patent attorney or patent specialist.')}
            </p>
          )}
          {verdict.rationale && (
            <p className="text-xs leading-relaxed">{verdict.rationale}</p>
          )}
        </div>
      ) : (
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-400 italic">
          {t('ftoClaimsAnalysis.noVerdict', 'No verdict available.')}
        </div>
      )}

      {/* ── Feature blocks ── */}
      {features.length === 0 ? (
        <p className="text-xs text-slate-400 italic">
          {t('ftoClaimsAnalysis.noFeatures', 'No features found in the claim analysis artifact.')}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
            {t('ftoClaimsAnalysis.featuresTitle', 'Feature-level analysis')}
          </h3>
          {features.map(feature => {
            const mapping = mappingById[feature.feature_id];
            const fverdict = verdictById[feature.feature_id];
            return (
              <FeatureBlock
                key={feature.feature_id}
                feature={feature}
                mapping={mapping}
                verdict={fverdict}
                t={t}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── FeatureBlock ──────────────────────────────────────────────────────────────

interface FeatureBlockProps {
  feature: RefinedFeature;
  mapping?: FeatureMapping;
  verdict?: FeatureVerdict;
  t: (key: string, defaultValue: string, opts?: Record<string, unknown>) => string;
}

const FeatureBlock: React.FC<FeatureBlockProps> = ({ feature, mapping, verdict, t }) => {
  const classLabel = CLASS_LABEL[feature.feature_class] ?? feature.feature_class;
  const classColor = CLASS_COLOR[feature.feature_class] ?? 'bg-slate-100 text-slate-600';
  const relationLabel = RELATION_LABEL[feature.relation] ?? feature.relation;
  const relationColor = RELATION_COLOR[feature.relation] ?? 'text-slate-500';
  const mark = mapping?.mark;
  const indentClass = feature.parent_id ? 'ml-4 border-l-2 border-slate-200 pl-2' : '';

  return (
    <div className={`rounded border border-slate-200 bg-white px-3 py-2 ${indentClass}`}>

      {/* Line 1: feature ID + text */}
      <div className="flex items-start gap-1.5 mb-1.5">
        <span className="text-[10px] font-mono text-slate-400 mt-0.5 flex-shrink-0">{feature.feature_id}</span>
        <span className="text-xs text-slate-800 leading-snug">{feature.text}</span>
      </div>

      {/* Line 2: pills row */}
      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
        {/* Classification */}
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${classColor}`}>
          {classLabel}
        </span>

        {/* Relation */}
        <span className={`text-[10px] font-medium ${relationColor}`}>
          {relationLabel}
          {feature.is_optional && <span className="ml-0.5 text-slate-400">(opt.)</span>}
        </span>

        {/* Score */}
        {mark !== undefined && (
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${markColor(mark)}`}>
            {t('ftoClaimsAnalysis.score', 'Score')}: {mark}/5
          </span>
        )}

        {/* is_present verdict */}
        {verdict && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
            verdict.is_present
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {verdict.is_present
              ? t('ftoClaimsAnalysis.reproduced', 'Reproduced')
              : t('ftoClaimsAnalysis.notReproduced', 'Not reproduced')}
            <span className="ml-1 opacity-70">{verdict.confidence}%</span>
          </span>
        )}

        {/* DOE: not implemented */}
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-400">
          {t('ftoClaimsAnalysis.doeNotStudied', 'DOE: not studied')}
        </span>
      </div>

      {/* FCM */}
      {feature.construed_meaning && (
        <div className="mb-1.5">
          <span className="text-[10px] font-semibold text-slate-500 mr-1">
            {t('ftoClaimsAnalysis.fcmLabel', 'FCM:')}
          </span>
          <span className="text-[11px] text-slate-600 italic">{feature.construed_meaning}</span>
        </div>
      )}

      {/* Evidence */}
      {mapping?.evidence && (
        <div className="mb-1.5 bg-slate-50 rounded px-2 py-1">
          <span className="text-[10px] font-semibold text-slate-500 mr-1">
            {t('ftoClaimsAnalysis.evidenceLabel', 'Evidence:')}
          </span>
          <span className="text-[11px] text-slate-600">{mapping.evidence}</span>
          {mapping.target_location && (
            <span className="ml-1.5 text-[10px] text-slate-400">({mapping.target_location})</span>
          )}
        </div>
      )}

      {/* LLM rationale */}
      {verdict?.rationale && (
        <div>
          <span className="text-[10px] font-semibold text-slate-500 mr-1">
            {t('ftoClaimsAnalysis.rationaleLabel', 'Rationale:')}
          </span>
          <span className="text-[11px] text-slate-700">{verdict.rationale}</span>
        </div>
      )}
    </div>
  );
};

export default FtoClaimsAnalysisPane;
